import { post, get } from './request'

// 微信支付参数
export interface WechatPayParams {
  appId: string
  timeStamp: string
  nonceStr: string
  package: string
  signType: string
  paySign: string
}

// 创建支付订单参数
export interface CreatePaymentDTO {
  orderNo: string
  amount: number
  description: string
  openid: string
}

// 支付结果
export interface PaymentResult {
  appId: string
  timeStamp: string
  nonceStr: string
  package: string
  signType: string
  paySign: string
  orderNo: string
  amount: number
}

// 支付状态
export interface PaymentStatus {
  orderNo: string
  tradeState: string
  tradeStateDesc: string
  transactionId?: string
  totalFee?: number
  timeEnd?: string
}

export const paymentApi = {
  /**
   * 创建微信支付订单
   */
  createWechatOrder(data: CreatePaymentDTO) {
    return post<PaymentResult>('/payment/wechat/create', data)
  },

  /**
   * 查询支付状态
   */
  queryPaymentStatus(orderNo: string) {
    return get<PaymentStatus>(`/payment/wechat/query/${orderNo}`)
  },

  /**
   * 申请退款
   */
  applyRefund(data: {
    transactionId: string
    amount: number
    totalAmount?: number
    reason: string
  }) {
    return post('/payment/wechat/refund', data)
  }
}

/**
 * 调起微信支付（微信公众号JSAPI）
 * @param params 微信支付参数
 * @returns Promise<boolean> 支付是否成功
 */
export function invokeWechatPay(params: WechatPayParams): Promise<boolean> {
  return new Promise((resolve, reject) => {
    // 检查是否在微信环境中
    const isWeixin = /micromessenger/i.test(navigator.userAgent)

    if (!isWeixin) {
      reject(new Error('请在微信客户端中打开以使用微信支付'))
      return
    }

    // 检查微信JS-SDK是否已加载
    if (typeof (window as any).WeixinJSBridge === 'undefined') {
      // 等待WeixinJSBridge加载
      document.addEventListener('WeixinJSBridgeReady', () => {
        doWechatPay(params, resolve, reject)
      }, false)
    } else {
      doWechatPay(params, resolve, reject)
    }
  })
}

/**
 * 执行微信支付
 */
function doWechatPay(
  params: WechatPayParams,
  resolve: (value: boolean) => void,
  reject: (reason: Error) => void
) {
  const WeixinJSBridge = (window as any).WeixinJSBridge

  if (!WeixinJSBridge) {
    reject(new Error('微信支付环境初始化失败'))
    return
  }

  WeixinJSBridge.invoke(
    'getBrandWCPayRequest',
    {
      appId: params.appId,
      timeStamp: params.timeStamp,
      nonceStr: params.nonceStr,
      package: params.package,
      signType: params.signType,
      paySign: params.paySign
    },
    (res: any) => {
      if (res.err_msg === 'get_brand_wcpay_request:ok') {
        // 支付成功
        resolve(true)
      } else if (res.err_msg === 'get_brand_wcpay_request:cancel') {
        // 用户取消支付
        reject(new Error('用户取消支付'))
      } else {
        // 支付失败
        reject(new Error(res.err_desc || '支付失败'))
      }
    }
  )
}

/**
 * 检查是否在微信环境中
 */
export function isWechatEnvironment(): boolean {
  return /micromessenger/i.test(navigator.userAgent)
}

/**
 * 获取微信用户OpenID
 * 注意：需要在微信授权后才能获取到openid
 */
export function getWechatOpenid(): string | null {
  // 从本地存储中获取openid
  return localStorage.getItem('wechat_openid')
}

/**
 * 保存微信用户OpenID
 */
export function setWechatOpenid(openid: string): void {
  localStorage.setItem('wechat_openid', openid)
}

export default paymentApi
